import { ElementRef, Injectable } from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, Observable } from 'rxjs';
import { MergeTagPickerConfig } from './merge-tag-picker.model';
import { MergeTagPickerOverlayComponent } from './merge-tag-picker-overlay/merge-tag-picker-overlay.component';
import { CartItemType } from '../../../../../../api/src/cart/project-cart-item.entity';
import { CreativeUnitDimensionsUnit } from '../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';

const DEFAULT_ACTIVE_GROUP = {
	id: 'example-group-id',
	name: 'Sample Audience Group',
	type: CartItemType.AD_UNIT,
	thumbnailUrl: 'https://example.com/thumbnails/sample-group.jpg',
	locale: {
		code: 'en-US',
		label: 'English (United States)'
	},
	customizations: [],
	tags: ['priority-audience', 'north-america']
};

const DEFAULT_ACTIVE_CREATIVE_UNIT = {
	id: 'creative-unit-example',
	name: 'Sample Modular Video',
	packageId: 'example-package-id',
	description: 'Fallback creative unit shown when no active unit is supplied.',
	dimensions: {
		width: 1080,
		height: 1920,
		defaultDisplayUnit: CreativeUnitDimensionsUnit.PIXELS
	},
	tags: ['demo', 'vertical-video'],
	layers: [],
	customizations: [],
	variables: {
		headline: 'Your Brand Headline',
		callToAction: 'Shop Now'
	},
	variableOverrides: {},
	items: []
};

export interface MergeTagPickerOpenOptions {
	anchor?: () => DOMRect | null | undefined;
	anchorElement?: ElementRef<Element> | Element | (() => Element | null | undefined);
	data: Record<string, unknown> | null;
	config?: MergeTagPickerConfig | null;
	offset?: [number, number];
	avoidArea?: () => DOMRect | null | undefined;
	placement?: string;
	onSelect?: (value: string) => void;
	onOverride?: () => void; // merge tag session has been overridden by another input
	onClose?: () => void;
	onInvalid?: (message: string) => void;
}

export interface MergeTagPickerSession {
	updateAnchor(anchor: () => DOMRect | null | undefined): void;
	updateData(data: Record<string, unknown> | null): void;
	close(): void;
	resetManualPosition(): void;
}

interface MergeTagPickerOverlayCallbacks {
	select?: (value: string) => void;
	close?: () => void;
	override?: () => void;
	invalid?: (message: string) => void;
}

interface MergeTagPickerOverlayInternalState {
	visible: boolean;
	data: Record<string, unknown> | null;
	config: MergeTagPickerConfig | null;
	anchor: (() => DOMRect | null | undefined) | null;
	placement: string;
	activePlacement: string;
	offset: { skid: number; distance: number };
	avoidArea?: () => DOMRect | null | undefined;
	manualPosition: { left: number; top: number } | null;
	callbacks: MergeTagPickerOverlayCallbacks;
}

export interface MergeTagPickerOverlayViewState {
	visible: boolean;
	data: Record<string, unknown> | null;
	config: MergeTagPickerConfig | null;
}

interface OverlayOverflow {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

class MergeTagPickerSessionRef implements MergeTagPickerSession {
	constructor(private readonly service: MergeTagPickerOverlayService) {}

	updateAnchor(anchor: () => DOMRect | null | undefined): void {
		this.service.updateAnchor(anchor);
	}

	updateData(data: Record<string, unknown> | null): void {
		this.service.updateData(data);
	}

	close(): void {
		this.service.close();
	}

	resetManualPosition(): void {
		this.service.resetManualPosition();
	}
}

@Injectable({ providedIn: 'root' })
export class MergeTagPickerOverlayService {
	private overlayRef?: OverlayRef;
	private componentInstance?: MergeTagPickerOverlayComponent;
	private readonly viewportMargin = 12;

	private internalState: MergeTagPickerOverlayInternalState = {
		visible: false,
		data: null,
		config: null,
		anchor: null,
		placement: 'bottom-start',
		activePlacement: 'bottom-start',
		offset: { skid: 0, distance: 8 },
		manualPosition: null,
		callbacks: {}
	};

	private readonly viewStateSubject = new BehaviorSubject<MergeTagPickerOverlayViewState>({
		visible: false,
		data: null,
		config: null
	});

	readonly viewState$: Observable<MergeTagPickerOverlayViewState> = this.viewStateSubject.asObservable();

	constructor(private readonly overlay: Overlay) {}

	isSessionActive(): boolean {
		return this.internalState?.visible ?? false;
	}

	open(options: MergeTagPickerOpenOptions): MergeTagPickerSession {
		this.ensureOverlay();

		const placement = options.placement ?? 'bottom-start';
		const anchorFactory = this.resolveAnchorFactory(options);
		if (!anchorFactory) {
			console.warn('MergeTagPickerOverlayService.open called without an anchor; overlay may not be positioned correctly.');
		}

		if (this.internalState.visible) {
			this.handleOverrideSession();
		}

		const enrichedData = this.enrichDataWithDefaults(options.data);

		this.internalState = {
			visible: true,
			data: enrichedData,
			config: options.config ?? null,
			anchor: anchorFactory,
			placement,
			activePlacement: placement,
			offset: this.normalizeOffset(options.offset),
			avoidArea: options.avoidArea,
			manualPosition: null,
			callbacks: {
				select: options.onSelect,
				close: options.onClose,
				override: options.onOverride,
				invalid: options.onInvalid
			}
		};

		this.emitViewState();
		this.applyOverlayVisibility(true);
		this.repositionOverlay();
		if (typeof window !== 'undefined') {
			requestAnimationFrame(() => this.repositionOverlay());
		}
		this.focusSearchInput();

		return new MergeTagPickerSessionRef(this);
	}

	updateAnchor(anchor: () => DOMRect | null | undefined): void {
		this.internalState = {
			...this.internalState,
			anchor
		};

		if (!this.internalState.manualPosition) {
			this.repositionOverlay();
		}
	}

	updateData(data: Record<string, unknown> | null): void {
		const enrichedData = this.enrichDataWithDefaults(data);
		this.internalState = {
			...this.internalState,
			data: enrichedData
		};
		this.emitViewState();
		this.repositionOverlay();
	}

	close(): void {
		if (!this.internalState.visible) {
			return;
		}
		const callbacks = this.internalState.callbacks;
		const basePlacement = this.internalState.placement;
		this.internalState = {
			...this.internalState,
			visible: false,
			manualPosition: null,
			activePlacement: basePlacement
		};
		this.emitViewState();
		this.applyOverlayVisibility(false);
		this.destroyOverlay();
		callbacks.close?.();
	}

	resetManualPosition(): void {
		if (!this.internalState.manualPosition) {
			return;
		}
		this.internalState = {
			...this.internalState,
			manualPosition: null
		};
		this.repositionOverlay();
	}

	handleTagSelected(value: string): void {
		this.internalState.callbacks.select?.(value);
	}

	handleInvalid(message: string): void {
		this.internalState.callbacks.invalid?.(message);
	}

	handleOverrideSession(): void {
		this.internalState.callbacks.override?.();
	}

	handleRequestClose(): void {
		this.close();
	}

	updateManualPosition(position: { left: number; top: number }): void {
		const clamped = this.clampPosition(position.left, position.top);
		this.internalState = {
			...this.internalState,
			manualPosition: { ...clamped }
		};
		this.applyOverlayPosition(clamped.left, clamped.top);
	}

	repositionOverlay(): void {
		if (!this.overlayRef) {
			return;
		}

		const size = this.getOverlayDimensions();
		const manual = this.internalState.manualPosition;
		if (manual) {
			const clampedManual = this.clampPosition(manual.left, manual.top, size);
			this.internalState = {
				...this.internalState,
				manualPosition: { ...clampedManual }
			};
			this.applyOverlayPosition(clampedManual.left, clampedManual.top);
			return;
		}

		const anchorRect = this.internalState.anchor?.();
		if (!anchorRect) {
			return;
		}

		const preferredPlacement = this.internalState.placement;
		let activePlacement = preferredPlacement;
		let desired = this.calculateAnchoredPosition(anchorRect, preferredPlacement, size);
		let overflow = this.computeOverflow(desired, size);

		if (this.shouldTryFallback(preferredPlacement, overflow)) {
			const fallbackPlacement = this.getFallbackPlacement(preferredPlacement);
			if (fallbackPlacement) {
				const fallbackDesired = this.calculateAnchoredPosition(anchorRect, fallbackPlacement, size);
				const fallbackOverflow = this.computeOverflow(fallbackDesired, size);
				if (this.totalOverflow(fallbackOverflow) < this.totalOverflow(overflow)) {
					desired = fallbackDesired;
					overflow = fallbackOverflow;
					activePlacement = fallbackPlacement;
				}
			}
		}

		// avoid covering the input/wrapper area
		desired = this.nudgeAwayFromAvoidArea(desired, size, activePlacement);

		const clamped = this.clampPosition(desired.left, desired.top, size);
		this.internalState = {
			...this.internalState,
			activePlacement,
			manualPosition: null
		};
		this.applyOverlayPosition(clamped.left, clamped.top);
	}

	private rectsOverlap(a: { left: number; top: number; width: number; height: number }, b: DOMRect): boolean {
		const aRight = a.left + a.width;
		const aBottom = a.top + a.height;
		return !(aRight < b.left || a.left > b.right || aBottom < b.top || a.top > b.bottom);
	}

	private nudgeAwayFromAvoidArea(
		desired: { left: number; top: number },
		size: { width: number; height: number },
		activePlacement: string
	): { left: number; top: number } {
		const avoidRect = this.internalState.avoidArea?.() ?? null;
		if (!avoidRect) return desired;

		const margin = this.viewportMargin;
		const base = this.getBasePlacement(activePlacement);

		// If overlapping, push the overlay away based on current base placement
		const candidate = { ...desired };
		const overlayBox = { left: candidate.left, top: candidate.top, width: size.width, height: size.height };

		if (!this.rectsOverlap(overlayBox, avoidRect)) {
			return candidate;
		}

		switch (base) {
			case 'bottom': {
				// Place below the avoid area
				candidate.top = avoidRect.bottom + margin;
				break;
			}
			case 'top': {
				// Place above the avoid area
				candidate.top = avoidRect.top - size.height - margin;
				break;
			}
			case 'right': {
				// Place to the right of the avoid area
				candidate.left = avoidRect.right + margin;
				break;
			}
			case 'left': {
				// Place to the left of the avoid area
				candidate.left = avoidRect.left - size.width - margin;
				break;
			}
		}

		return candidate;
	}

	focusSearchInput(): void {
		this.componentInstance?.focusSearchInput();
	}

	registerComponent(instance: MergeTagPickerOverlayComponent): void {
		this.componentInstance = instance;
		this.applyOverlayVisibility(this.internalState.visible);
		if (this.internalState.visible && typeof window !== 'undefined') {
			requestAnimationFrame(() => this.repositionOverlay());
		}
	}

	unregisterComponent(instance: MergeTagPickerOverlayComponent): void {
		if (this.componentInstance === instance) {
			this.componentInstance = undefined;
		}
	}

	private destroyOverlay(): void {
		if (this.overlayRef) {
			try {
				this.overlayRef.dispose();
			} finally {
				this.overlayRef = null;
				this.componentInstance = null;
			}
		}
	}

	private ensureOverlay(forceRecreate: boolean = false): void {
		if (this.overlayRef && !forceRecreate) {
			return;
		}
		if (forceRecreate) {
			this.destroyOverlay();
		}

		this.overlayRef = this.overlay.create({
			hasBackdrop: false,
			scrollStrategy: this.overlay.scrollStrategies.reposition(),
			positionStrategy: this.overlay.position().global()
		});

		const portal = new ComponentPortal(MergeTagPickerOverlayComponent);
		this.overlayRef.attach(portal);
		this.applyOverlayVisibility(false);
	}

	private emitViewState(): void {
		this.viewStateSubject.next({
			visible: this.internalState.visible,
			data: this.internalState.data,
			config: this.internalState.config
		});
	}

	private normalizeOffset(offset?: [number, number]): { skid: number; distance: number } {
		if (!offset || offset.length !== 2) {
			return { skid: 0, distance: 8 };
		}

		const [skid, distance] = offset;
		return {
			skid: Number.isFinite(skid) ? Number(skid) : 0,
			distance: Number.isFinite(distance) ? Number(distance) : 0
		};
	}

	private applyOverlayPosition(left: number, top: number): void {
		if (!this.overlayRef) {
			return;
		}

		const safePosition = this.clampPosition(left, top);
		this.overlayRef.updatePositionStrategy(
			this.overlay
				.position()
				.global()
				.left(`${Math.round(safePosition.left)}px`)
				.top(`${Math.round(safePosition.top)}px`)
		);
	}

	private applyOverlayVisibility(visible: boolean): void {
		if (!this.overlayRef) {
			return;
		}

		const element = this.overlayRef.overlayElement as HTMLElement | null;
		if (!element) {
			return;
		}

		element.style.pointerEvents = visible ? 'auto' : 'none';
		element.style.visibility = visible ? 'visible' : 'hidden';
	}

	private resolveAnchorFactory(options: MergeTagPickerOpenOptions): (() => DOMRect | null | undefined) | null {
		if (typeof options.anchor === 'function') {
			return options.anchor;
		}

		const source = options.anchorElement;
		if (!source) {
			return null;
		}

		if (typeof source === 'function') {
			return () => {
				const element = source();
				return element ? element.getBoundingClientRect() : null;
			};
		}

		if (source instanceof ElementRef) {
			return () => source.nativeElement?.getBoundingClientRect?.() ?? null;
		}

		const nativeElementCandidate = (source as any)?.nativeElement as Element | null | undefined;
		if (nativeElementCandidate && typeof nativeElementCandidate.getBoundingClientRect === 'function') {
			return () => nativeElementCandidate.getBoundingClientRect();
		}

		if (source instanceof Element) {
			const element = source;
			return () => element.getBoundingClientRect();
		}

		return null;
	}

	private getOverlayDimensions(): { width: number; height: number } {
		const element = this.overlayRef?.overlayElement;
		if (!element) {
			return { width: 0, height: 0 };
		}

		const rect = element.getBoundingClientRect();
		const width = rect.width || element.offsetWidth || 0;
		const height = rect.height || element.offsetHeight || 0;
		return { width, height };
	}

	private getViewportBounds(): { width: number; height: number } | null {
		if (typeof window === 'undefined') {
			return null;
		}

		return {
			width: window.innerWidth || 0,
			height: window.innerHeight || 0
		};
	}

	private calculateAnchoredPosition(anchorRect: DOMRect, placement: string, size: { width: number; height: number }): {
		left: number;
		top: number;
	} {
		const base = this.getBasePlacement(placement);
		const [, alignmentRaw] = placement.split('-') as [string, string?];
		const alignment = alignmentRaw ?? 'center';
		const { skid, distance } = this.internalState.offset;
		const overlayWidth = size.width;
		const overlayHeight = size.height;

		switch (base) {
			case 'top': {
				const left = this.resolveHorizontalAlignment(anchorRect, overlayWidth, alignment, skid);
				const top = anchorRect.top - overlayHeight - distance;
				return { left, top };
			}
			case 'bottom': {
				const left = this.resolveHorizontalAlignment(anchorRect, overlayWidth, alignment, skid);
				const top = anchorRect.bottom + distance;
				return { left, top };
			}
			case 'left': {
				const top = this.resolveVerticalAlignment(anchorRect, overlayHeight, alignment, skid);
				const left = anchorRect.left - overlayWidth - distance;
				return { left, top };
			}
			case 'right':
			default: {
				const top = this.resolveVerticalAlignment(anchorRect, overlayHeight, alignment, skid);
				const left = anchorRect.right + distance;
				return { left, top };
			}
		}
	}

	private resolveHorizontalAlignment(anchorRect: DOMRect, overlayWidth: number, alignment: string, skid: number): number {
		switch (alignment) {
			case 'end':
				return anchorRect.right - overlayWidth + skid;
			case 'center':
				return anchorRect.left + anchorRect.width / 2 - overlayWidth / 2 + skid;
			case 'start':
			default:
				return anchorRect.left + skid;
		}
	}

	private resolveVerticalAlignment(anchorRect: DOMRect, overlayHeight: number, alignment: string, skid: number): number {
		switch (alignment) {
			case 'end':
				return anchorRect.bottom - overlayHeight + skid;
			case 'center':
				return anchorRect.top + anchorRect.height / 2 - overlayHeight / 2 + skid;
			case 'start':
			default:
				return anchorRect.top + skid;
		}
	}

	private getBasePlacement(placement: string): 'top' | 'bottom' | 'left' | 'right' {
		const [base] = placement.split('-') as [string, string?];
		if (base === 'top' || base === 'left' || base === 'right') {
			return base;
		}
		return 'bottom';
	}

	private getFallbackPlacement(placement: string): string | null {
		const base = this.getBasePlacement(placement);
		const [, alignmentRaw] = placement.split('-') as [string, string?];
		const alignmentSuffix = alignmentRaw ? `-${alignmentRaw}` : '';

		switch (base) {
			case 'bottom':
				return `top${alignmentSuffix}`;
			case 'top':
				return `bottom${alignmentSuffix}`;
			case 'left':
				return `right${alignmentSuffix}`;
			case 'right':
				return `left${alignmentSuffix}`;
			default:
				return null;
		}
	}

	private computeOverflow(position: { left: number; top: number }, size: { width: number; height: number }): OverlayOverflow {
		const viewport = this.getViewportBounds();
		if (!viewport) {
			return { top: 0, bottom: 0, left: 0, right: 0 };
		}

		const margin = this.viewportMargin;
		const overflowTop = Math.max(0, margin - position.top);
		const overflowLeft = Math.max(0, margin - position.left);
		const overflowBottom = Math.max(0, position.top + size.height - (viewport.height - margin));
		const overflowRight = Math.max(0, position.left + size.width - (viewport.width - margin));

		return {
			top: overflowTop,
			bottom: overflowBottom,
			left: overflowLeft,
			right: overflowRight
		};
	}

	private shouldTryFallback(placement: string, overflow: OverlayOverflow): boolean {
		switch (this.getBasePlacement(placement)) {
			case 'bottom':
				return overflow.bottom > 0;
			case 'top':
				return overflow.top > 0;
			case 'left':
				return overflow.left > 0;
			case 'right':
				return overflow.right > 0;
			default:
				return false;
		}
	}

	private totalOverflow(overflow: OverlayOverflow): number {
		return overflow.top + overflow.bottom + overflow.left + overflow.right;
	}

	private clampPosition(left: number, top: number, size?: { width: number; height: number }): { left: number; top: number } {
		const viewport = this.getViewportBounds();
		if (!viewport) {
			return { left, top };
		}

		const dimensions = size ?? this.getOverlayDimensions();
		const margin = this.viewportMargin;
		const width = Math.max(dimensions.width, 0);
		const height = Math.max(dimensions.height, 0);
		const maxLeft = Math.max(margin, viewport.width - width - margin);
		const maxTop = Math.max(margin, viewport.height - height - margin);
		const clampedLeft = Math.min(Math.max(left, margin), maxLeft);
		const clampedTop = Math.min(Math.max(top, margin), maxTop);

		return { left: clampedLeft, top: clampedTop };
	}

	private enrichDataWithDefaults(data: Record<string, unknown> | null): Record<string, unknown> | null {
		if (!data || typeof data !== 'object') {
			return data;
		}

		const enriched = { ...data };

		// Add default activeGroup if it exists in data but is undefined or empty
		if ('activeGroup' in enriched && (!enriched.activeGroup || (typeof enriched.activeGroup === 'object' && Object.keys(enriched.activeGroup as object).length === 0))) {
			enriched.activeGroup = DEFAULT_ACTIVE_GROUP;
		}

		// Add default activeCreativeUnit if it exists in data but is undefined or empty
		if ('activeCreativeUnit' in enriched && (!enriched.activeCreativeUnit || (typeof enriched.activeCreativeUnit === 'object' && Object.keys(enriched.activeCreativeUnit as object).length === 0))) {
			enriched.activeCreativeUnit = DEFAULT_ACTIVE_CREATIVE_UNIT;
		}

		return enriched;
	}
}
